import { useRef, useState } from "react";
import { Link } from "react-router";
import { api } from "../api";
import { withBuildingScope } from "../ui/building-scope";

/** Desktop outcome: a confirmed completion and a failed reload have separate recovery paths. */
export function InspectionSourceOutcome({
  action,
  inspection: s,
  onSaved,
  disabled,
}: {
  action: any;
  inspection: any;
  onSaved: () => Promise<unknown>;
  disabled: boolean;
}) {
  const [busy, setBusy] = useState(false),
    [message, setMessage] = useState(""),
    [pendingView, setPendingView] = useState(false),
    pending = useRef<{ body: string; confirmed: boolean } | undefined>(
      undefined,
    ),
    inFlight = useRef(false);
  async function retry() {
    if (inFlight.current || !pending.current) return;
    inFlight.current = true;
    setBusy(true);
    setMessage("");
    try {
      if (!pending.current.confirmed) {
        try {
          await api(
            withBuildingScope(
              "/api/operations/programmes/actions/" + action.id + "/accept",
              action.development_id,
            ),
            { method: "POST", body: pending.current.body },
          );
          pending.current.confirmed = true;
        } catch (e: any) {
          if (e.statusCode >= 400 && e.statusCode < 500) {
            pending.current = undefined;
            setPendingView(false);
          }
          throw e;
        }
      }
      await onSaved();
      pending.current = undefined;
      setPendingView(false);
      setMessage(
        "Visual observations completed. Follow-up work and the next month remain separate.",
      );
    } catch (e: any) {
      setMessage(
        (pending.current?.confirmed
          ? "Completion saved. Reload the current inspection outcome: "
          : "") + e.message,
      );
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }
  const missing = [
    !s.sourceCurrent && "Review the current monthly source and review team.",
    !s.accepted && "Complete coordinator and final Property Manager review.",
    !s.followUpsLinked &&
      "Link every Concern and accepted photo exception to its follow-up.",
    !s.mandatoryCovered &&
      "Complete and accept the exact return for mandatory areas not inspected.",
    s.accessOpen && "Resolve genuine Access Actions.",
    !s.attendanceCurrent && "Review the retained inspection attendance.",
  ].filter(Boolean);
  return (
    <section aria-label="Inspection cycle outcome">
      <h2>Inspection outcome</h2>
      <p>
        {s.completed
          ? s.openFollowUps
            ? "Visual observations completed, actions outstanding."
            : "Visual observations completed."
          : "Report acceptance and completion of this month are separate."}{" "}
        This records the visual visit; corrective and specialist work remains in
        its own workflow.
      </p>
      {s.openFollowUps > 0 && (
        <p>{s.openFollowUps} follow-up items remain open.</p>
      )}
      <Link
        to={withBuildingScope("/reports/" + s.reportId, action.development_id)}
      >
        Review original inspection, findings and return checks
      </Link>
      {!s.writesEnabled && (
        <p>Inspection completion changes are not enabled.</p>
      )}
      {!s.completed && (
        <>
          {!s.authority && (
            <p>
              The current designated final Property Manager completes this
              inspection.
            </p>
          )}
          {missing.length > 0 && (
            <ul>
              {missing.map((text, i) => (
                <li key={i}>{text}</li>
              ))}
            </ul>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (pending.current || inFlight.current) return;
              pending.current = {
                body: JSON.stringify({
                  expectedVersion: action.version,
                  idempotencyKey: crypto.randomUUID(),
                  reason: new FormData(e.currentTarget).get("reason") ?? "",
                }),
                confirmed: false,
              };
              setPendingView(true);
              void retry();
            }}
          >
            <fieldset
              disabled={disabled || busy || pendingView || !s.canComplete}
            >
              <legend>Complete this month's observations</legend>
              <label>
                Outcome note (optional)
                <textarea name="reason" maxLength={3000} />
              </label>
              <button>Complete visual observations</button>
            </fieldset>
          </form>
        </>
      )}
      {message && <p role="status">{message}</p>}
      {pendingView && !busy && (
        <button disabled={disabled} type="button" onClick={() => void retry()}>
          {pending.current?.confirmed
            ? "Reload saved inspection outcome"
            : "Retry retained inspection completion"}
        </button>
      )}
    </section>
  );
}
